import { isNil } from 'lodash';
import { View } from '@shared/components/View';
import { Dropdown, DropdownButton } from '@shared/components/dropdown';
import { List } from '@shared/components/filter/List';
import { ControlOption } from '@shared/components/filter/types';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { TextInput } from '@shared/components/textInput';
import { MBTextBody1, MBTextBody1Bold, MBTextBody2 } from '@shared/components/typography';
import { useWorkflowDispatch } from '@shared/scenes/workflows/context/context';
import { Divider } from '@shared/scenes/workflows/edit/components/optionsMenu/divider';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { Colors, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { PM60, useFeatureFlag } from '@shared/util/featureFlags';
import { i18n } from '@shared/util/i18n';

const MINIMUM_IN_SERIES_MIN = 1;
const MINIMUM_IN_SERIES_MAX = 100;

export function DateAutoExtendDefaultOptions({
  workflowTemplateId,
  component,
}: {
  workflowTemplateId: string;
  component: API.DateComponent;
}) {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const tagComponents = workflowTemplate?.components.filter(isTagComponent);
  const tagComponentOptions: ControlOption[] = (tagComponents ?? []).map(tagComponent => ({
    id: tagComponent.id,
    label: tagComponent.label ?? '',
    icon: tagComponent.icon as MBIconV2Name,
  }));
  const defaultTagComponentId = component.autoExtendDefault?.impliedFilterComponentId;
  const selectedTagComponentOption = tagComponentOptions?.find(
    to => to.id === defaultTagComponentId,
  );
  const dispatch = useWorkflowDispatch();
  const isPM60Enabled = useFeatureFlag(PM60);
  const minimumInSeries = component.autoExtendDefault?.minimumInSeries;
  const isMinimumInSeriesValid =
    isNil(minimumInSeries) ||
    (minimumInSeries >= MINIMUM_IN_SERIES_MIN && minimumInSeries <= MINIMUM_IN_SERIES_MAX);

  const handleMinimumInSeriesChange = (value: string) => {
    const numberValue = Number(value);
    if (isNaN(numberValue)) {
      return;
    }
    dispatch({
      type: 'updateComponent',
      args: {
        ...component,
        autoExtendDefault: {
          ...component.autoExtendDefault,
          minimumInSeries: numberValue || undefined,
        },
      } as API.DateComponent,
    });
  };

  const handleTagComponentOptionChange = (option: ControlOption) => {
    dispatch({
      type: 'updateComponent',
      args: {
        ...component,
        autoExtendDefault: {
          impliedFilterComponentId: defaultTagComponentId === option.id ? null : option.id,
          minimumInSeries: component.autoExtendDefault?.minimumInSeries,
        },
      } as API.DateComponent,
    });
  };

  if (!isPM60Enabled) {
    return null;
  }

  return (
    <>
      <View paddingHorizontal={3}>
        <View paddingVertical={1}>
          <MBTextBody1Bold>
            {i18n('workflowTemplate.component.dateAndRepeat.defaultOptions.autoExtendTitle')}
          </MBTextBody1Bold>
          <View row marginTop={1}>
            <TextInput
              value={
                !isNil(minimumInSeries) ? String(minimumInSeries) : String(MINIMUM_IN_SERIES_MIN)
              }
              onChangeText={handleMinimumInSeriesChange}
              error={
                isMinimumInSeriesValid
                  ? undefined
                  : i18n(
                      'workflowTemplate.component.dateAndRepeat.defaultOptions.minimumInSeriesError',
                    )
              }
              placeholder="1-100"
              style={{ width: grid(10), marginBottom: grid(1) }}
            />
          </View>
          <MBTextBody2>
            {i18n('workflowTemplate.component.dateAndRepeat.defaultOptions.minimumInSeries', {
              count: minimumInSeries ?? 0,
            })}
          </MBTextBody2>
          <View paddingBottom={2} paddingTop={1}>
            <View row centerVertically flex>
              <View flex>
                <Dropdown
                  button={({ onClick, isOpen }) => (
                    <DropdownButton onClick={onClick} open={isOpen}>
                      <View row centerVertically style={{ maxWidth: '78%' }}>
                        {selectedTagComponentOption?.icon && (
                          <MBIconV2
                            name={selectedTagComponentOption.icon as MBIconV2Name}
                            color={Colors.v2.purple}
                            size={grid(3)}
                            style={{ marginRight: grid(1) }}
                          />
                        )}
                        <MBTextBody1
                          numberOfLines={1}
                          color={
                            !isNil(selectedTagComponentOption) ? Colors.v2.purple : Colors.v2.gray50
                          }
                        >
                          {selectedTagComponentOption?.label ||
                            i18n('card.actions.groups.noneSelected')}
                        </MBTextBody1>
                      </View>
                    </DropdownButton>
                  )}
                  closeOnSelect
                >
                  <List
                    options={tagComponentOptions}
                    selected={selectedTagComponentOption}
                    onSelect={handleTagComponentOptionChange}
                  />
                </Dropdown>
              </View>
            </View>
          </View>
        </View>
      </View>
      <View paddingHorizontal={2}>
        <Divider />
      </View>
    </>
  );
}
